/**
 * PDI/PDC Dashboard - Planos de Desenvolvimento Individual e Coletivo
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { pdiAPI, pdcAPI, employeesAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Separator } from '../../components/ui/separator';
import { Progress } from '../../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Target,
  Users,
  Plus,
  Search,
  Filter,
  ChevronRight,
  Calendar,
  CheckCircle2,
  Clock,
  AlertCircle,
  Loader2,
  User,
  Building2,
  Briefcase,
  BookOpen,
  Award,
  TrendingUp,
  Edit,
  Trash2,
  Eye,
  Play,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  rascunho: 'bg-slate-100 text-slate-700',
  ativo: 'bg-blue-100 text-blue-700',
  concluido: 'bg-green-100 text-green-700',
  cancelado: 'bg-red-100 text-red-700'
};

const statusLabels = {
  rascunho: 'Rascunho',
  ativo: 'Ativo',
  concluido: 'Concluído',
  cancelado: 'Cancelado'
};

const prioridadeColors = {
  alta: 'bg-red-100 text-red-700',
  media: 'bg-yellow-100 text-yellow-700',
  baixa: 'bg-green-100 text-green-700'
};

export default function PDIDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('pdi');
  const [loading, setLoading] = useState(true);
  const [pdis, setPdis] = useState([]);
  const [pdcs, setPdcs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filtros, setFiltros] = useState(null);
  const [templates, setTemplates] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Dialogs
  const [showPDIDialog, setShowPDIDialog] = useState(false);
  const [showPDCDialog, setShowPDCDialog] = useState(false);
  const [selectedPDI, setSelectedPDI] = useState(null);
  const [selectedPDC, setSelectedPDC] = useState(null);
  
  // Form states
  const [pdiForm, setPdiForm] = useState({
    employee_id: '',
    titulo: '',
    descricao: '',
    periodo_inicio: '',
    periodo_fim: '',
    objetivos_carreira: '',
    competencias_desenvolver: [],
    acoes: []
  });
  
  const [pdcForm, setPdcForm] = useState({
    titulo: '',
    descricao: '',
    periodo_inicio: '',
    periodo_fim: '',
    tipo_selecao: 'empresa',
    departamento: '',
    cargo: '',
    gestor_id: '',
    justificativa: '',
    resultado_esperado: '',
    acoes: []
  });
  
  const [previewParticipantes, setPreviewParticipantes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pdisRes, pdcsRes, employeesRes, filtrosRes, templatesRes] = await Promise.all([
        pdiAPI.getAll(),
        pdcAPI.getAll(),
        employeesAPI.getAll({ situacao: 'ativo' }),
        pdcAPI.getFiltros(),
        pdiAPI.getTemplates()
      ]);
      
      setPdis(pdisRes.data);
      setPdcs(pdcsRes.data);
      setEmployees(employeesRes.data.employees || employeesRes.data || []);
      setFiltros(filtrosRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePDI = async () => {
    if (!pdiForm.employee_id || !pdiForm.titulo || !pdiForm.periodo_inicio || !pdiForm.periodo_fim) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    
    setSaving(true);
    try {
      await pdiAPI.create(pdiForm);
      toast.success('PDI criado com sucesso!');
      setShowPDIDialog(false);
      resetPDIForm();
      fetchData();
    } catch (error) {
      toast.error('Erro ao criar PDI');
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePDC = async () => {
    if (!pdcForm.titulo || !pdcForm.periodo_inicio || !pdcForm.periodo_fim) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    
    setSaving(true);
    try {
      await pdcAPI.create(pdcForm);
      toast.success('PDC criado com sucesso!');
      setShowPDCDialog(false);
      resetPDCForm();
      fetchData();
    } catch (error) {
      toast.error('Erro ao criar PDC');
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewParticipantes = async () => {
    try {
      const res = await pdcAPI.previewParticipantes({
        tipo_selecao: pdcForm.tipo_selecao,
        departamento: pdcForm.departamento,
        cargo: pdcForm.cargo,
        gestor_id: pdcForm.gestor_id
      });
      setPreviewParticipantes(res.data.participantes || []);
    } catch (error) {
      console.error('Error previewing participants:', error);
    }
  };

  const handleGerarPDIs = async (pdcId) => {
    try {
      const res = await pdcAPI.gerarPDIs(pdcId);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Erro ao gerar PDIs');
    }
  };

  const handleDeletePDI = async (id) => {
    if (!window.confirm('Confirma exclusão do PDI?')) return;
    try {
      await pdiAPI.delete(id);
      toast.success('PDI excluído');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleDeletePDC = async (id) => {
    if (!window.confirm('Confirma exclusão do PDC?')) return;
    try {
      await pdcAPI.delete(id);
      toast.success('PDC excluído');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const resetPDIForm = () => {
    setPdiForm({
      employee_id: '',
      titulo: '',
      descricao: '',
      periodo_inicio: '',
      periodo_fim: '',
      objetivos_carreira: '',
      competencias_desenvolver: [],
      acoes: []
    });
  };

  const resetPDCForm = () => {
    setPdcForm({
      titulo: '',
      descricao: '',
      periodo_inicio: '',
      periodo_fim: '',
      tipo_selecao: 'empresa',
      departamento: '',
      cargo: '',
      gestor_id: '',
      justificativa: '',
      resultado_esperado: '',
      acoes: []
    });
    setPreviewParticipantes([]);
  };

  const addAcaoToPDI = () => {
    setPdiForm(prev => ({
      ...prev,
      acoes: [...prev.acoes, {
        titulo: '',
        descricao: '',
        categoria: 'tecnico',
        tipo_acao: 'treinamento',
        prazo: '',
        prioridade: 'media'
      }]
    }));
  };

  const addAcaoToPDC = () => {
    setPdcForm(prev => ({
      ...prev,
      acoes: [...prev.acoes, {
        titulo: '',
        descricao: '',
        categoria: 'tecnico',
        tipo_acao: 'treinamento',
        prazo: '',
        prioridade: 'media'
      }]
    }));
  };

  const filteredPDIs = pdis.filter(p => 
    (p.employee_nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     p.titulo?.toLowerCase().includes(searchTerm.toLowerCase())) &&
    (!statusFilter || p.status === statusFilter)
  );

  const filteredPDCs = pdcs.filter(p =>
    p.titulo?.toLowerCase().includes(searchTerm.toLowerCase()) &&
    (!statusFilter || p.status === statusFilter)
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/company')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="font-bold text-slate-900">Planos de Desenvolvimento</h1>
                  <p className="text-xs text-slate-500">PDI & PDC</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                onClick={() => { resetPDCForm(); setShowPDCDialog(true); }}
              >
                <Users className="w-4 h-4 mr-2" />
                Novo PDC
              </Button>
              <Button 
                className="bg-cyan-600 hover:bg-cyan-700"
                onClick={() => { resetPDIForm(); setShowPDIDialog(true); }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo PDI
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{pdis.length}</p>
                  <p className="text-sm text-slate-500">PDIs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{pdcs.length}</p>
                  <p className="text-sm text-slate-500">PDCs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {pdis.filter(p => p.status === 'ativo').length}
                  </p>
                  <p className="text-sm text-slate-500">Ativos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {Math.round(pdis.reduce((acc, p) => acc + (p.percentual_progresso || 0), 0) / (pdis.length || 1))}%
                  </p>
                  <p className="text-sm text-slate-500">Progresso Médio</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-slate-200 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar por nome ou título..."
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="rascunho">Rascunho</SelectItem>
                  <SelectItem value="ativo">Ativo</SelectItem>
                  <SelectItem value="concluido">Concluído</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="pdi" className="gap-2">
              <User className="w-4 h-4" />
              PDI Individual ({filteredPDIs.length})
            </TabsTrigger>
            <TabsTrigger value="pdc" className="gap-2">
              <Users className="w-4 h-4" />
              PDC Coletivo ({filteredPDCs.length})
            </TabsTrigger>
          </TabsList>

          {/* PDI Tab */}
          <TabsContent value="pdi">
            <Card className="border-slate-200">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Funcionário</TableHead>
                      <TableHead>Título</TableHead>
                      <TableHead>Período</TableHead>
                      <TableHead>Progresso</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPDIs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                          Nenhum PDI encontrado
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredPDIs.map((pdi) => (
                        <TableRow key={pdi.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-slate-500" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{pdi.employee_nome}</p>
                                <p className="text-xs text-slate-500">{pdi.employee_cargo}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <p className="font-medium">{pdi.titulo}</p>
                            <p className="text-xs text-slate-500">{pdi.total_acoes || 0} ações</p>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm">{pdi.periodo_inicio?.slice(0, 10)}</p>
                            <p className="text-xs text-slate-500">a {pdi.periodo_fim?.slice(0, 10)}</p>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Progress value={pdi.percentual_progresso || 0} className="w-20 h-2" />
                              <span className="text-sm text-slate-600">{pdi.percentual_progresso || 0}%</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={statusColors[pdi.status]}>
                              {statusLabels[pdi.status]}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="icon" onClick={() => navigate(`/company/pdi/${pdi.id}`)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDeletePDI(pdi.id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PDC Tab */}
          <TabsContent value="pdc">
            <Card className="border-slate-200">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Título</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Participantes</TableHead>
                      <TableHead>Período</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPDCs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                          Nenhum PDC encontrado
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredPDCs.map((pdc) => (
                        <TableRow key={pdc.id}>
                          <TableCell>
                            <p className="font-medium">{pdc.titulo}</p>
                            <p className="text-xs text-slate-500">{pdc.total_acoes || 0} ações</p>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {pdc.tipo_selecao === 'empresa' && 'Toda Empresa'}
                              {pdc.tipo_selecao === 'departamento' && pdc.departamento}
                              {pdc.tipo_selecao === 'cargo' && pdc.cargo}
                              {pdc.tipo_selecao === 'gestor' && pdc.gestor_nome}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-slate-400" />
                              <span>{pdc.total_participantes || 0}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm">{pdc.periodo_inicio?.slice(0, 10)}</p>
                            <p className="text-xs text-slate-500">a {pdc.periodo_fim?.slice(0, 10)}</p>
                          </TableCell>
                          <TableCell>
                            <Badge className={statusColors[pdc.status]}>
                              {statusLabels[pdc.status]}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {pdc.status === 'rascunho' && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleGerarPDIs(pdc.id)}
                                  className="text-green-600"
                                >
                                  <Play className="w-4 h-4 mr-1" />
                                  Ativar
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" onClick={() => handleDeletePDC(pdc.id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* PDI Dialog */}
      <Dialog open={showPDIDialog} onOpenChange={setShowPDIDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novo PDI</DialogTitle>
            <DialogDescription>Plano de Desenvolvimento Individual</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Funcionário *</Label>
              <Select 
                value={pdiForm.employee_id} 
                onValueChange={(v) => setPdiForm(p => ({ ...p, employee_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o funcionário" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.nome} - {emp.cargo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Título *</Label>
              <Input
                value={pdiForm.titulo}
                onChange={(e) => setPdiForm(p => ({ ...p, titulo: e.target.value }))}
                placeholder="Ex: Desenvolvimento de Liderança Q1 2026"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Início *</Label>
                <Input
                  type="date"
                  value={pdiForm.periodo_inicio}
                  onChange={(e) => setPdiForm(p => ({ ...p, periodo_inicio: e.target.value }))}
                />
              </div>
              <div>
                <Label>Fim *</Label>
                <Input
                  type="date"
                  value={pdiForm.periodo_fim}
                  onChange={(e) => setPdiForm(p => ({ ...p, periodo_fim: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <Label>Objetivos de Carreira</Label>
              <Textarea
                value={pdiForm.objetivos_carreira}
                onChange={(e) => setPdiForm(p => ({ ...p, objetivos_carreira: e.target.value }))}
                placeholder="Descreva os objetivos de carreira do funcionário..."
                rows={3}
              />
            </div>
            
            <Separator />
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Ações de Desenvolvimento</Label>
                <Button variant="outline" size="sm" onClick={addAcaoToPDI}>
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              </div>
              
              {pdiForm.acoes.map((acao, idx) => (
                <Card key={idx} className="mb-2 border-slate-200">
                  <CardContent className="p-3 space-y-2">
                    <Input
                      value={acao.titulo}
                      onChange={(e) => {
                        const newAcoes = [...pdiForm.acoes];
                        newAcoes[idx].titulo = e.target.value;
                        setPdiForm(p => ({ ...p, acoes: newAcoes }));
                      }}
                      placeholder="Título da ação"
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <Select
                        value={acao.categoria || 'tecnico'}
                        onValueChange={(v) => {
                          const newAcoes = [...pdiForm.acoes];
                          newAcoes[idx].categoria = v;
                          setPdiForm(p => ({ ...p, acoes: newAcoes }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(templates?.categorias || ['tecnico', 'comportamental', 'lideranca', 'conhecimento']).map(c => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={acao.tipo_acao || 'treinamento'}
                        onValueChange={(v) => {
                          const newAcoes = [...pdiForm.acoes];
                          newAcoes[idx].tipo_acao = v;
                          setPdiForm(p => ({ ...p, acoes: newAcoes }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(templates?.tipos_acao || ['treinamento', 'mentoria', 'projeto', 'certificacao', 'leitura', 'experiencia']).map(t => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={acao.prioridade || 'media'}
                        onValueChange={(v) => {
                          const newAcoes = [...pdiForm.acoes];
                          newAcoes[idx].prioridade = v;
                          setPdiForm(p => ({ ...p, acoes: newAcoes }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="alta">Alta</SelectItem>
                          <SelectItem value="media">Média</SelectItem>
                          <SelectItem value="baixa">Baixa</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPDIDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreatePDI} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Criar PDI
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDC Dialog */}
      <Dialog open={showPDCDialog} onOpenChange={setShowPDCDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novo PDC</DialogTitle>
            <DialogDescription>Plano de Desenvolvimento Coletivo</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Título *</Label>
              <Input
                value={pdcForm.titulo}
                onChange={(e) => setPdcForm(p => ({ ...p, titulo: e.target.value }))}
                placeholder="Ex: Capacitação em Liderança - Gestores"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Início *</Label>
                <Input
                  type="date"
                  value={pdcForm.periodo_inicio}
                  onChange={(e) => setPdcForm(p => ({ ...p, periodo_inicio: e.target.value }))}
                />
              </div>
              <div>
                <Label>Fim *</Label>
                <Input
                  type="date"
                  value={pdcForm.periodo_fim}
                  onChange={(e) => setPdcForm(p => ({ ...p, periodo_fim: e.target.value }))}
                />
              </div>
            </div>
            
            <Separator />
            
            <div>
              <Label>Selecionar Participantes Por</Label>
              <Select 
                value={pdcForm.tipo_selecao}
                onValueChange={(v) => {
                  setPdcForm(p => ({ ...p, tipo_selecao: v }));
                  setTimeout(handlePreviewParticipantes, 100);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="empresa">Toda Empresa</SelectItem>
                  <SelectItem value="departamento">Departamento</SelectItem>
                  <SelectItem value="cargo">Cargo</SelectItem>
                  <SelectItem value="gestor">Encarregado/Gestor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {pdcForm.tipo_selecao === 'departamento' && (
              <div>
                <Label>Departamento</Label>
                <Select 
                  value={pdcForm.departamento}
                  onValueChange={(v) => {
                    setPdcForm(p => ({ ...p, departamento: v }));
                    setTimeout(handlePreviewParticipantes, 100);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {filtros?.departamentos?.map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {pdcForm.tipo_selecao === 'cargo' && (
              <div>
                <Label>Cargo</Label>
                <Select 
                  value={pdcForm.cargo}
                  onValueChange={(v) => {
                    setPdcForm(p => ({ ...p, cargo: v }));
                    setTimeout(handlePreviewParticipantes, 100);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {filtros?.cargos?.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {pdcForm.tipo_selecao === 'gestor' && (
              <div>
                <Label>Encarregado/Gestor</Label>
                <Select 
                  value={pdcForm.gestor_id}
                  onValueChange={(v) => {
                    setPdcForm(p => ({ ...p, gestor_id: v }));
                    setTimeout(handlePreviewParticipantes, 100);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {filtros?.gestores?.map(g => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.nome} ({g.subordinados_count} subordinados)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {previewParticipantes.length > 0 && (
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm font-medium text-slate-700 mb-2">
                  {previewParticipantes.length} participantes selecionados
                </p>
                <div className="flex flex-wrap gap-1">
                  {previewParticipantes.slice(0, 10).map(p => (
                    <Badge key={p.id} variant="secondary" className="text-xs">
                      {p.nome}
                    </Badge>
                  ))}
                  {previewParticipantes.length > 10 && (
                    <Badge variant="secondary" className="text-xs">
                      +{previewParticipantes.length - 10} mais
                    </Badge>
                  )}
                </div>
              </div>
            )}
            
            <div>
              <Label>Justificativa</Label>
              <Textarea
                value={pdcForm.justificativa}
                onChange={(e) => setPdcForm(p => ({ ...p, justificativa: e.target.value }))}
                placeholder="Por que este plano é necessário?"
                rows={2}
              />
            </div>
            
            <Separator />
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Ações de Desenvolvimento</Label>
                <Button variant="outline" size="sm" onClick={addAcaoToPDC}>
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              </div>
              
              {pdcForm.acoes.map((acao, idx) => (
                <Card key={idx} className="mb-2 border-slate-200">
                  <CardContent className="p-3 space-y-2">
                    <Input
                      value={acao.titulo}
                      onChange={(e) => {
                        const newAcoes = [...pdcForm.acoes];
                        newAcoes[idx].titulo = e.target.value;
                        setPdcForm(p => ({ ...p, acoes: newAcoes }));
                      }}
                      placeholder="Título da ação"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Select
                        value={acao.categoria || 'tecnico'}
                        onValueChange={(v) => {
                          const newAcoes = [...pdcForm.acoes];
                          newAcoes[idx].categoria = v;
                          setPdcForm(p => ({ ...p, acoes: newAcoes }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(templates?.categorias || ['tecnico', 'comportamental', 'lideranca', 'conhecimento']).map(c => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={acao.tipo_acao || 'treinamento'}
                        onValueChange={(v) => {
                          const newAcoes = [...pdcForm.acoes];
                          newAcoes[idx].tipo_acao = v;
                          setPdcForm(p => ({ ...p, acoes: newAcoes }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(templates?.tipos_acao || ['treinamento', 'mentoria', 'projeto', 'certificacao', 'leitura', 'experiencia']).map(t => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPDCDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreatePDC} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Criar PDC
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
